import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import Icon from './AppIcon';
import { fetchOrderById } from '../lib/adminApi';

const statusStyles = {
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-sky-100 text-sky-700',
  ready_for_delivery: 'bg-indigo-100 text-indigo-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-rose-100 text-rose-700',
};

export default function OrderDetailsModal({ orderId, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    setError(null);
    fetchOrderById(orderId)
      .then(setOrder)
      .catch((e) => setError(e?.message || 'Failed to load order'))
      .finally(() => setLoading(false));
  }, [orderId]);

  const items = Array.isArray(order?.order_items) ? order.order_items : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-card max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">
            Order details {orderId ? `· ${String(orderId).slice(0, 8)}…` : ''}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-8rem)] px-6 py-4 space-y-6">
          {loading && (
            <div className="py-12 text-center text-muted-foreground">Loading…</div>
          )}
          {error && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
              {error}
            </div>
          )}
          {!loading && !error && order && (
            <>
              <section>
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Overview</h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusStyles[order.status] ?? 'bg-muted'}`}>
                      {order.status?.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="font-semibold text-foreground">
                      R{Number(order.total_amount ?? 0).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Event date</p>
                    <p className="text-foreground">
                      {order.event_date ? format(new Date(order.event_date), 'dd MMM yyyy') : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Guests</p>
                    <p className="text-foreground">{order.guests ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Customer</p>
                    <p className="text-foreground">{order.customer?.email ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Caterer</p>
                    <p className="text-foreground">{order.caterer?.business_name ?? '—'}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-xs text-muted-foreground">Delivery</p>
                    <p className="text-foreground">{order.delivery_location || '—'}</p>
                  </div>
                </div>
              </section>

              {order.escrow && (
                <section>
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Escrow</h3>
                  <div className="flex flex-wrap gap-4 rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">
                    <span>
                      <span className="text-muted-foreground">Status:</span>{' '}
                      <span className="font-medium">{order.escrow.escrow_status ?? '—'}</span>
                    </span>
                    <span>
                      <span className="text-muted-foreground">Net:</span>{' '}
                      R{Number(order.escrow.caterer_net_amount ?? 0).toLocaleString()}
                    </span>
                    {order.escrow.payment_reference && (
                      <span>
                        <span className="text-muted-foreground">Ref:</span>{' '}
                        <span className="font-mono text-xs">{order.escrow.payment_reference}</span>
                      </span>
                    )}
                  </div>
                </section>
              )}

              <section>
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Line items ({items.length})
                </h3>
                {items.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
                    No line items recorded.
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-border">
                    <table className="min-w-full divide-y divide-border text-sm">
                      <thead className="bg-muted/30 text-muted-foreground">
                        <tr>
                          <th className="px-4 py-2 text-left font-semibold">Item</th>
                          <th className="px-4 py-2 text-right font-semibold">Qty</th>
                          <th className="px-4 py-2 text-right font-semibold">Unit price</th>
                          <th className="px-4 py-2 text-right font-semibold">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/80">
                        {items.map((row) => (
                          <tr key={row.id}>
                            <td className="px-4 py-2 text-foreground">{row.name || 'Item'}</td>
                            <td className="px-4 py-2 text-right">{row.quantity ?? 0}</td>
                            <td className="px-4 py-2 text-right">
                              R{Number(row.unit_price ?? 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-right font-medium">
                              R{Number(row.subtotal ?? (row.unit_price || 0) * (row.quantity || 0)).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
